import { z } from 'zod';
import {
  conversationMessageSchema,
  conversationPositionSchema,
  conversationReadWatermarkShape,
  hasValidConversationReadWatermarks,
  type ChatMessage,
} from './chat-response';
import { conversationTypingEventSchema, PRESENCE_EXPIRY_MAX_MS } from './typing';

export const CONVERSATION_REALTIME_EVENT_KINDS = [
  'message',
  'read_state',
  'typing',
  'presence',
] as const;
export const DURABLE_CONVERSATION_REALTIME_EVENT_KINDS = ['message', 'read_state'] as const;
export const CONVERSATION_REALTIME_OUTBOX_STATUSES = [
  'pending',
  'in_flight',
  'dispatched',
  'attention_required',
] as const;
export const CONVERSATION_REALTIME_FAILURE_CLASSES = [
  'timeout',
  'throttled',
  'provider_unavailable',
  'authorization',
  'invalid_payload',
  'configuration',
  'internal',
] as const;
export const conversationRealtimeEventKindSchema = z.enum(CONVERSATION_REALTIME_EVENT_KINDS);
export const durableConversationRealtimeEventKindSchema = z.enum(
  DURABLE_CONVERSATION_REALTIME_EVENT_KINDS,
);
export const conversationRealtimeOutboxStatusSchema = z.enum(CONVERSATION_REALTIME_OUTBOX_STATUSES);
export const conversationRealtimeFailureClassSchema = z.enum(CONVERSATION_REALTIME_FAILURE_CLASSES);

export const CONVERSATION_MESSAGE_DELIVERY_MODES = ['inline', 'cursor_only'] as const;
export const conversationMessageDeliveryModeSchema = z.enum(CONVERSATION_MESSAGE_DELIVERY_MODES);

// The delivery policy is the caller's minimization decision; the delivery mode
// above is the resulting wire shape. `inline_within_budget` ships the message
// body when it fits the transport budget. `cursor_only` withholds the body and
// the sender identity unconditionally — clients converge on the position and
// fetch content over the authenticated read path — so conversations whose text
// must never transit the realtime plane select it by classification.
export const CONVERSATION_MESSAGE_DELIVERY_POLICIES = [
  'inline_within_budget',
  'cursor_only',
] as const;
export const conversationMessageDeliveryPolicySchema = z.enum(
  CONVERSATION_MESSAGE_DELIVERY_POLICIES,
);
export type ConversationMessageDeliveryPolicy = z.infer<
  typeof conversationMessageDeliveryPolicySchema
>;

export const CONVERSATION_CLIENT_ERROR_CODES = [
  'invalid_input',
  'unauthorized',
  'temporarily_unavailable',
  'internal_error',
] as const;
export const conversationClientErrorCodeSchema = z.enum(CONVERSATION_CLIENT_ERROR_CODES);

export const conversationEnvironmentKeySchema = z
  .string()
  .min(1)
  .max(63)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

const baseEventShape = {
  eventId: z.string().uuid(),
  environmentKey: conversationEnvironmentKeySchema,
  conversationId: z.string().uuid(),
  participantId: z.string().uuid(),
  emittedAt: z.string().datetime({ offset: true }),
};

export const inlineConversationMessageEventSchema = z
  .object({
    ...baseEventShape,
    kind: z.literal('message'),
    position: conversationPositionSchema,
    senderParticipantId: z.string().uuid().nullable(),
    deliveryMode: z.literal('inline'),
    message: conversationMessageSchema,
  })
  .strict()
  .refine((value) => value.message.conversationId === value.conversationId, {
    message: 'message belongs to another conversation',
    path: ['message', 'conversationId'],
  })
  .refine((value) => value.message.position === value.position, {
    message: 'message position does not match the event position',
    path: ['message', 'position'],
  })
  .refine((value) => value.message.senderParticipantId === value.senderParticipantId, {
    message: 'message sender does not match the event sender',
    path: ['senderParticipantId'],
  });

export const cursorOnlyConversationMessageEventSchema = z
  .object({
    ...baseEventShape,
    kind: z.literal('message'),
    position: conversationPositionSchema,
    senderParticipantId: z.string().uuid().nullable(),
    deliveryMode: z.literal('cursor_only'),
  })
  .strict();

export const conversationReadStateRealtimePayloadSchema = z
  .object({
    participantId: z.string().uuid(),
    ...conversationReadWatermarkShape,
  })
  .strict()
  .refine(hasValidConversationReadWatermarks, {
    message: 'read watermark exceeds delivered watermark',
  });

export const conversationReadStateRealtimeEventSchema = z
  .object({
    ...baseEventShape,
    kind: z.literal('read_state'),
    position: conversationPositionSchema,
    readState: conversationReadStateRealtimePayloadSchema,
  })
  .strict();

export const conversationTypingRealtimePayloadSchema = z
  .object({
    publisherParticipantId: z.string().uuid(),
    state: conversationTypingEventSchema.shape.state,
    ttlMs: conversationTypingEventSchema.shape.ttlMs,
  })
  .strict();

export const conversationTypingRealtimeEventSchema = z
  .object({
    ...baseEventShape,
    kind: z.literal('typing'),
    typing: conversationTypingRealtimePayloadSchema,
  })
  .strict();

export const conversationPresenceRealtimePayloadSchema = z
  .object({
    publisherParticipantId: z.string().uuid(),
    ttlMs: z.number().int().min(1).max(PRESENCE_EXPIRY_MAX_MS),
  })
  .strict();

export const conversationPresenceRealtimeEventSchema = z
  .object({
    ...baseEventShape,
    kind: z.literal('presence'),
    presence: conversationPresenceRealtimePayloadSchema,
  })
  .strict();

export const conversationRealtimeEventSchema = z.union([
  inlineConversationMessageEventSchema,
  cursorOnlyConversationMessageEventSchema,
  conversationReadStateRealtimeEventSchema,
  conversationTypingRealtimeEventSchema,
  conversationPresenceRealtimeEventSchema,
]);
export type ConversationRealtimeEvent = z.infer<typeof conversationRealtimeEventSchema>;
export type ConversationMessageRealtimeEvent =
  | z.infer<typeof inlineConversationMessageEventSchema>
  | z.infer<typeof cursorOnlyConversationMessageEventSchema>;

/**
 * Parse the GraphQL subscription projection into the provider-neutral event
 * contract. AppSync materializes unselected union branches as `null` and
 * returns AWSJSON fields as encoded strings, while the shared contract models
 * absent branches and decoded message parts. Keeping that transport
 * normalization beside the schema gives every real-plane client one strict
 * boundary instead of duplicating subtly different coercion code.
 */
export function parseConversationRealtimeGraphqlEvent(value: unknown): ConversationRealtimeEvent {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('invalid conversation realtime GraphQL event');
  }
  const event: Record<string, unknown> = { ...value };
  for (const branch of ['message', 'readState', 'typing', 'presence'] as const) {
    if (event[branch] === null) delete event[branch];
  }
  if (event.message && typeof event.message === 'object' && !Array.isArray(event.message)) {
    const message: Record<string, unknown> = { ...event.message };
    if (typeof message.parts === 'string') {
      try {
        const parsedParts: unknown = JSON.parse(message.parts);
        message.parts = parsedParts;
      } catch {
        throw new Error('invalid conversation realtime GraphQL event');
      }
    }
    event.message = message;
  }
  const parsed = conversationRealtimeEventSchema.safeParse(event);
  if (!parsed.success) throw new Error('invalid conversation realtime GraphQL event');
  return parsed.data;
}

export const conversationRealtimeClientErrorSchema = z
  .object({
    code: conversationClientErrorCodeSchema,
    retryable: z.boolean(),
  })
  .strict();
export type ConversationRealtimeClientError = z.infer<typeof conversationRealtimeClientErrorSchema>;

// The trusted publish mutation carries the event as AWSJSON, so the event JSON
// is itself encoded inside the GraphQL variables envelope. Measuring the
// canonical object alone undercounts quotes, backslashes, control characters,
// and nested GenUI JSON. Reserving a fixed envelope allowance here keeps both
// the publish request and the selected subscription frame below the configured
// transport budget without coupling the shared contract to one provider SDK.
export const CONVERSATION_REALTIME_ENVELOPE_RESERVE_BYTES = 4_096;

export function encodedConversationRealtimeEnvelopeBytes(event: ConversationRealtimeEvent): number {
  const awsJson = JSON.stringify(event);
  const representativeEnvelope = JSON.stringify({ event: awsJson });
  return (
    new TextEncoder().encode(representativeEnvelope).byteLength +
    CONVERSATION_REALTIME_ENVELOPE_RESERVE_BYTES
  );
}

export function projectMessageDelivery(
  event: Omit<
    z.input<typeof inlineConversationMessageEventSchema>,
    'deliveryMode' | 'message' | 'senderParticipantId'
  >,
  message: ChatMessage,
  payloadBudgetBytes: number,
  deliveryPolicy: ConversationMessageDeliveryPolicy = 'inline_within_budget',
): ConversationMessageRealtimeEvent {
  const inline = inlineConversationMessageEventSchema.parse({
    ...event,
    senderParticipantId: message.senderParticipantId,
    deliveryMode: 'inline',
    message,
  });
  if (
    deliveryPolicy === 'inline_within_budget' &&
    encodedConversationRealtimeEnvelopeBytes(inline) <= payloadBudgetBytes
  ) {
    return inline;
  }
  const { message: _message, deliveryMode: _deliveryMode, ...cursor } = inline;
  return cursorOnlyConversationMessageEventSchema.parse({
    ...cursor,
    // A budget overflow is a transport fallback and keeps the sender; the
    // minimizing policy withholds it too, so the push plane carries only
    // convergence coordinates.
    senderParticipantId: deliveryPolicy === 'cursor_only' ? null : cursor.senderParticipantId,
    deliveryMode: 'cursor_only',
  });
}
